import { useCallback } from "react";
import { useLazyGetProductsQuery } from "../../masters/products/productsApi";

// Naming trap worth stating once: on a PRODUCT, `unit`/`alterUnit` are the
// meaningless legacy DECIMAL columns (values like 4188, 1217 - never a real
// reference to anything) and `unitLabel` is the real text unit added in
// Batch82_ProductUnitLabel.sql. On a LINE ITEM, `unit` IS that text label.
// So a line's unit always comes from product.unitLabel, never product.unit.
//
// Picking a product by hand already auto-fills the unit inside
// LineItemsEditor's ProductPicker.onChange. The gap this closes is every
// path that builds lines WITHOUT the picker - prefilling a Stock-In from its
// LPO, a Return from its Stock-Out, a Quotation from a prototype, a
// Vehicle-Maintenance LPO from its spare items, or simply reloading a saved
// document for edit. Those copy whatever unit the source document happened to
// store, which is blank for anything created before that product got its
// unitLabel (and for locked rows the picker is hidden entirely, so the
// operator has no way to trigger the auto-fill either).
//
// Deliberately fill-only: a unit already on the line always wins, so this
// never rewrites what a document actually recorded - it only resolves blanks.

/** One product-code -> unitLabel lookup, deduped, for a single branch. */
const fetchUnitLabels = async (searchProducts, branchId, productCodes) => {
  const map = new Map();
  await Promise.all(
    productCodes.map(async (code) => {
      try {
        const matches = await searchProducts({ branchId: Number(branchId), search: String(code) }).unwrap();
        const product = (matches || []).find((p) => String(p.productCode) === String(code));
        if (product?.unitLabel) map.set(String(code), product.unitLabel);
      } catch {
        // A lookup failure must never block the prefill - the line just keeps
        // its blank unit, exactly as it behaved before this existed.
      }
    })
  );
  return map;
};

/**
 * Returns `fillMissingUnits(rows, branchId)` - resolves `unit` from the
 * Product Master for any row that has a productCode but no unit yet.
 * Resolves to the same array (identity unchanged) when there is nothing to
 * fill, so callers can skip a redundant setState.
 */
export const useFillMissingUnits = () => {
  const [searchProducts] = useLazyGetProductsQuery();

  return useCallback(
    async (rows, branchId) => {
      if (!branchId || !rows?.length) return rows;
      const pending = [...new Set(rows.filter((r) => r.productCode && !r.unit).map((r) => String(r.productCode)))];
      if (pending.length === 0) return rows;

      const units = await fetchUnitLabels(searchProducts, branchId, pending);
      if (units.size === 0) return rows;

      return rows.map((r) => (r.productCode && !r.unit ? { ...r, unit: units.get(String(r.productCode)) || "" } : r));
    },
    [searchProducts]
  );
};
